import React, { useState, useEffect } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Grid, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { suggestTimeSpan } from '../../../utils/suggestTimeSpan';
const classes = {
    grid: {
        padding: '8px',
        textAlign: 'center',
    },
    timepicker: { maxWidth: '180px' },
    yearpicker: { maxWidth: '120px' },
};
var PARAMETER;
(function (PARAMETER) {
    PARAMETER["START"] = "START";
    PARAMETER["END"] = "END";
})(PARAMETER || (PARAMETER = {}));
export const DateWidget = ({ setTemporalParameter, datasetsWithTimeSpan, }) => {
    const [advancedMode, advancedModeOn] = useState(false);
    const handleChange = (event) => {
        advancedModeOn(event.target.checked);
    };
    // Suggest initial parameters and sync opendap parameter state
    const { proposedMinDate, proposedMaxDate } = suggestTimeSpan(datasetsWithTimeSpan);
    useEffect(() => {
        setTemporalParameter({
            start_time: proposedMinDate.replace(/-/g, ''),
            stop_time: proposedMaxDate.replace(/-/g, ''),
        });
    }, [proposedMinDate, proposedMaxDate]);
    /**
     * Date pickers require you to have a local state (that changes with selection) in date format
     * API call requires to have date as a string, this uses the setTemporalParameter hook from props.
     * Note that any change handler MUST be of type (date: Date) => void;
     */
    const proposeDateToPicker = () => {
        const proposedStartDate = new Date(proposedMinDate.slice(0, 10));
        const proposedEndDate = new Date(proposedMaxDate.slice(0, 10));
        return { proposedStartDate, proposedEndDate };
    };
    const proposeYearToPicker = () => {
        const proposedStartYear = new Date(`${proposedMinDate.slice(0, 4)}-01-01`);
        const proposedEndYear = new Date(`${proposedMaxDate.slice(0, 4)}-12-31`);
        return { proposedStartYear, proposedEndYear };
    };
    // Month and day are set fixed and cannot be changed:
    const [startYear, setLocalStartYear] = useState(proposeYearToPicker().proposedStartYear);
    const [endYear, setLocalEndYear] = useState(proposeYearToPicker().proposedEndYear);
    // Full control over day, month, year:
    const [startDate, setLocalStartDate] = useState(proposeDateToPicker().proposedStartDate);
    const [endDate, setLocalEndDate] = useState(proposeDateToPicker().proposedEndDate);
    const parseFullDate = (value) => {
        const day = `0${value.getDate()}`.slice(-2);
        const month = `0${value.getMonth() + 1}`.slice(-2);
        const year = value.getFullYear();
        return `${year}${month}${day}`;
    };
    const updateParameterState = (startDateString, endDateString) => setTemporalParameter({
        start_time: startDateString.replace(/-/g, ''),
        stop_time: endDateString.replace(/-/g, ''),
    });
    const handleDateChange = (date, parameter) => {
        if (parameter === PARAMETER.START && date) {
            updateParameterState(parseFullDate(date), parseFullDate(endDate));
            setLocalStartDate(new Date(date));
        }
        if (parameter === PARAMETER.END && date) {
            updateParameterState(parseFullDate(startDate), parseFullDate(date));
            setLocalEndDate(new Date(date));
        }
    };
    const handleYearChange = (date, parameter) => {
        if (parameter === PARAMETER.START && date) {
            const startDateString = `${date.getFullYear()}-01-01`;
            updateParameterState(startDateString, parseFullDate(endYear));
            setLocalStartYear(new Date(date));
        }
        if (parameter === PARAMETER.END && date) {
            const endDateString = `${date.getFullYear()}-12-31`;
            updateParameterState(parseFullDate(startYear), endDateString);
            setLocalEndYear(new Date(date));
        }
    };
    return (React.createElement(Grid, { container: true, sx: { justifyContent: 'space-around' }, item: true, xs: 12 },
        React.createElement(Grid, { component: 'label', container: true, alignItems: 'center', xs: 12, sx: classes.grid },
            React.createElement(FormControlLabel, { style: { padding: '5px 0' }, control: React.createElement(Switch, { color: 'primary', checked: advancedMode, onChange: handleChange, name: 'advancedMode' }), label: 'Advanced' })),
        advancedMode ? (React.createElement(React.Fragment, null,
            React.createElement(Grid, { item: true, xs: 6, sx: classes.grid },
                React.createElement(DatePicker, { label: 'Start date', value: startDate, inputFormat: 'dd/MM/yyyy', toolbarPlaceholder: '01/01/2020', minDate: new Date('0000-01-01'), maxDate: new Date('5000-12-31'), onChange: (date) => handleDateChange(date, PARAMETER.START), views: ['year', 'month', 'day'], openTo: 'year', renderInput: (params) => (React.createElement(TextField, Object.assign({ sx: classes.timepicker }, params, { required: true, variant: 'outlined' }))) })),
            React.createElement(Grid, { item: true, xs: 6, sx: classes.grid },
                React.createElement(DatePicker, { label: 'End date', value: endDate, inputFormat: 'dd/MM/yyyy', toolbarPlaceholder: '31/12/2020', minDate: new Date('0000-01-01'), maxDate: new Date('5000-12-31'), onChange: (date) => handleDateChange(date, PARAMETER.END), views: ['year', 'month', 'day'], openTo: 'year', renderInput: (params) => (React.createElement(TextField, Object.assign({ sx: classes.timepicker }, params, { required: true, variant: 'outlined' }))) })))) : (React.createElement(React.Fragment, null,
            React.createElement(Grid, { item: true, xs: 6, sx: classes.grid },
                React.createElement(DatePicker, { label: 'Start year', value: startYear, minDate: new Date('0000-01-01'), maxDate: new Date('5000-12-31'), onChange: (date) => handleYearChange(date, PARAMETER.START), views: ['year'], renderInput: (params) => (React.createElement(TextField, Object.assign({ sx: classes.yearpicker }, params, { required: true, variant: 'outlined' }))) })),
            React.createElement(Grid, { item: true, xs: 6, sx: classes.grid },
                React.createElement(DatePicker, { label: 'End year', value: endYear, minDate: new Date('0000-01-01'), maxDate: new Date('5000-12-31'), onChange: (date) => handleYearChange(date, PARAMETER.END), views: ['year'], renderInput: (params) => (React.createElement(TextField, Object.assign({ sx: classes.yearpicker }, params, { required: true, variant: 'outlined' }))) }))))));
};
